import { useQuery } from '@tanstack/react-query'
import { FC, useEffect, useRef, useState } from 'react'
import { DocumentTagEnum } from '../../../../common/interfaces'
import { customSwal } from '../../../../config/swal'
import { FundDraftService } from '../../../../services/FundDraftService'

interface IUploadFile {
  placeholder: string
  document_tag: DocumentTagEnum
  id: number | null
  constancy_filename: string
  constancy_url: string
  sign: boolean
}

const uploadFilesV2: IUploadFile[] = [
  {
    placeholder: 'Anexo de reglamento',
    document_tag: DocumentTagEnum.ANEXO_REGLAMENTO,
    id: null as number | null,
    constancy_filename: '',
    constancy_url: '',
    sign: false,
  },
  {
    placeholder: 'Prospecto simplificado',
    document_tag: DocumentTagEnum.PROSPECTO_SIMPLIFICADO,
    id: null as number | null,
    constancy_filename: '',
    constancy_url: '',
    sign: false,
  },
]

interface IProps {
  codFund: string
  dirtyFiles?: Partial<Record<DocumentTagEnum, File | null>>
  onDirtyChange?: (tag: DocumentTagEnum, file: File | null) => void
  dirtyRenames?: Partial<Record<DocumentTagEnum, string>>
  onRenameChange?: (tag: DocumentTagEnum, name: string, id: number) => void
  onRenameClear?: (tag: DocumentTagEnum) => void
  disableShow?: boolean
  disableUpload?: boolean
}

export const UploadFilesV2: FC<IProps> = ({
  codFund,
  dirtyFiles,
  onDirtyChange,
  dirtyRenames,
  onRenameChange,
  onRenameClear,
  disableShow = false,
  disableUpload = false,
}) => {
  const [files, setFiles] = useState(uploadFilesV2)
  const [openDropdown, setOpenDropdown] = useState<number | null>(null)
  const dropdownRefs = useRef<(HTMLDivElement | null)[]>([])
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  useQuery({
    queryKey: ['document-draft', codFund],
    queryFn: () => FundDraftService().getDocumentDraft(codFund),
    onSuccess: (data) => {
      setFiles(
        uploadFilesV2.map((uploadFile) => {
          const match = data.find((item) => item.document_tag === uploadFile.document_tag)
          return match
            ? {
                ...uploadFile,
                id: match.id,
                constancy_filename: match.constancy_filename ?? '',
                constancy_url: match.constancy_url,
              }
            : uploadFile
        })
      )
    },
    enabled: !!codFund,
  })

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (openDropdown === null) return
      const ref = dropdownRefs.current[openDropdown]
      if (ref && !ref.contains(e.target as Node)) setOpenDropdown(null)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openDropdown])

  const handlePreview = (url: string) => {
    if (!url) return
    window.open(url, '_blank')
    setOpenDropdown(null)
  }

  const handleDownload = async (file: IUploadFile) => {
    if (!file.id) return
    setOpenDropdown(null)
    try {
      const blob = await FundDraftService().downloadDocumentDraft(codFund, file.id)
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = file.constancy_filename || `document_${file.id}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(blobUrl)
    } catch {
      customSwal.fire({ title: 'Error al descargar el documento', icon: 'error' })
    }
  }

  const handleRename = async (file: IUploadFile) => {
    if (!file.id) return
    setOpenDropdown(null)
    const result = await customSwal.fire({
      title: 'Cambiar nombre',
      input: 'text',
      inputValue: dirtyRenames?.[file.document_tag] ?? file.constancy_filename,
      inputPlaceholder: 'Nombre del archivo',
      showCancelButton: true,
      confirmButtonText: 'Aceptar',
      cancelButtonText: 'Cancelar',
    })
    if (result.isConfirmed && result.value?.trim()) {
      onRenameChange?.(file.document_tag, result.value.trim(), file.id)
    }
  }

  const handleFileSelect = (tag: DocumentTagEnum, file: File) => {
    if (file.name.match(/[À-ÿ]/u)) {
      customSwal.fire({
        icon: 'error',
        title: 'El archivo no es válido, asegúrate que no contenga caracteres inválidos.',
      })
      return
    }
    onRenameClear?.(tag)
    onDirtyChange?.(tag, file)
  }

  return (
    <div className='card mt-10 p-10'>
      <div className='d-flex align-items-center justify-content-between'>
        <span className='card-title text-start mt-2 mb-5 text-gray-800 fs-2'>Adjuntos</span>
      </div>
      <table className='table table-row-dashed table-row-gray-300 gy-7'>
        <thead>
          <tr className='fw-bolder fs-6 text-gray-700'>
            <th>DOCUMENTO</th>
            <th style={{ width: 80 }}>ACCIÓN</th>
          </tr>
        </thead>
        <tbody>
          {files.map((file, index) => {
            const dirtyFile = dirtyFiles?.[file.document_tag]
            const dirtyRename = dirtyRenames?.[file.document_tag]
            const displayName = dirtyFile ? dirtyFile.name : dirtyRename ?? file.constancy_filename

            return (
              <tr key={index}>
                <td>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                    <span className='text-muted'>{file.placeholder}</span>
                    <span
                      style={{
                        fontSize: 11,
                        color: '#0066cc',
                        fontWeight: 600,
                        visibility: dirtyFile || dirtyRename ? 'visible' : 'hidden',
                      }}
                    >
                      {dirtyFile ? 'Sin guardar: nuevo archivo' : 'Sin guardar: cambio de nombre'}
                    </span>
                  </div>
                  <div
                    ref={(el) => (dropdownRefs.current[index] = el)}
                    style={{ position: 'relative', display: 'flex', alignItems: 'center' }}
                  >
                    <input
                      type='text'
                      className='form-control mb-0'
                      placeholder={file.placeholder}
                      value={displayName}
                      style={{ paddingRight: '48px' }}
                      disabled
                    />
                    <button
                      type='button'
                      onClick={() => setOpenDropdown(openDropdown === index ? null : index)}
                      disabled={!file.constancy_url || disableShow}
                      style={{
                        position: 'absolute',
                        right: 6,
                        background: '#3d8bcd',
                        border: 'none',
                        borderRadius: 6,
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: file.constancy_url && !disableShow ? 'pointer' : 'default',
                        opacity: file.constancy_url && !disableShow ? 1 : 0.4,
                      }}
                    >
                      <i className='bi bi-chevron-down' style={{ color: '#fff', fontSize: 13 }} />
                    </button>
                    {openDropdown === index && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          right: 0,
                          background: '#fff',
                          border: '1px solid #e1e3ea',
                          borderRadius: 8,
                          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                          zIndex: 100,
                          minWidth: 180,
                          padding: '4px 0',
                          marginTop: 4,
                        }}
                      >
                        <button
                          type='button'
                          style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'left',
                            background: 'none',
                            border: 'none',
                            fontSize: 14,
                            color: '#4b5563',
                            padding: '8px 16px 8px 20px',
                            cursor: 'pointer',
                          }}
                          onClick={() => handlePreview(file.constancy_url)}
                        >
                          Vista previa
                        </button>
                        <button
                          type='button'
                          style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'left',
                            background: 'none',
                            border: 'none',
                            fontSize: 14,
                            color: '#4b5563',
                            padding: '8px 16px 8px 20px',
                            cursor: 'pointer',
                          }}
                          onClick={() => handleDownload(file)}
                        >
                          Descargar
                        </button>
                        {!disableUpload && (
                          <button
                            type='button'
                            disabled={!!dirtyFiles?.[file.document_tag]}
                            style={{
                              display: 'block',
                              width: '100%',
                              textAlign: 'left',
                              background: 'none',
                              border: 'none',
                              fontSize: 14,
                              color: dirtyFiles?.[file.document_tag] ? '#9ca3af' : '#4b5563',
                              padding: '8px 16px 8px 20px',
                              cursor: dirtyFiles?.[file.document_tag] ? 'not-allowed' : 'pointer',
                            }}
                            onClick={() => handleRename(file)}
                          >
                            Cambiar nombre
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </td>
                <td className='text-center' style={{ verticalAlign: 'bottom' }}>
                  <span className='d-block text-muted' style={{ marginBottom: 5, visibility: 'hidden' }}>_</span>
                  <input
                    ref={(el) => (inputRefs.current[index] = el)}
                    type='file'
                    className='d-none'
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      if (f) handleFileSelect(file.document_tag, f)
                      e.target.value = ''
                    }}
                  />
                  <button
                    type='button'
                    disabled={!!dirtyRename || disableUpload}
                    style={{
                      background: 'transparent',
                      border: '1px solid #e1e3ea',
                      opacity: dirtyRename || disableUpload ? 0.4 : 1,
                      cursor: dirtyRename || disableUpload ? 'not-allowed' : 'pointer',
                    }}
                    className='btn btn-icon'
                    onClick={() => inputRefs.current[index]?.click()}
                  >
                    <i className='bi bi-upload text-muted fs-3' />
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
